import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/StatusBadge";
import { ReconciliationNoteButton } from "@/components/ReconciliationNoteButton";
import LedgerEntryFormDialog from "@/components/dialogs/LedgerEntryFormDialog";
import api, { API, fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Download, Trash2, Mail, Upload, Pencil, FileText } from "lucide-react";

export default function FinancialLedger() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCa = user?.role === "ca";
  const canViewAll = isAdmin || isCa;
  const [payroll, setPayroll] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForEmployee, setAddForEmployee] = useState(null);

  const load = () => {
    if (isAdmin) {
      api.get("/employees/ledger/all").then((r) => setPayroll(r.data)).catch(() => {});
      api.get("/clients/invoices/all").then((r) => setInvoices(r.data)).catch(() => {});
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
    } else if (isCa) {
      api.get("/clients/invoices/all").then((r) => setInvoices(r.data)).catch(() => {});
    } else {
      api.get("/employees/me").then((r) => api.get(`/employees/${r.data.id}/ledger`).then((res) => setPayroll(res.data)));
    }
  };

  useEffect(() => {
    load();
  }, [isAdmin, isCa]);

  const currency = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const deleteLedgerEntry = async (employeeId, entryId) => {
    try {
      await api.delete(`/employees/${employeeId}/ledger/${entryId}`);
      toast.success("Ledger entry deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteInvoice = async (clientId, invoiceId) => {
    try {
      await api.delete(`/clients/${clientId}/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const emailInvoice = async (clientId, invoiceId) => {
    try {
      const res = await api.post(`/clients/${clientId}/invoices/${invoiceId}/email`);
      toast.success(res.data.message || "Invoice emailed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadReceipt = async (employeeId, entryId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/${employeeId}/ledger/${entryId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteReceipt = async (employeeId, entryId) => {
    try {
      await api.delete(`/employees/${employeeId}/ledger/${entryId}/receipt`);
      toast.success("Receipt removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadInvoiceFile = async (clientId, invoiceId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clients/${clientId}/invoices/${invoiceId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice file saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteInvoiceFile = async (clientId, invoiceId) => {
    try {
      await api.delete(`/clients/${clientId}/invoices/${invoiceId}/file`);
      toast.success("Invoice file removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Financial Ledger">
      <Tabs defaultValue={isCa ? "invoices" : "payroll"}>
        <TabsList data-testid="ledger-tabs">
          {!isCa && <TabsTrigger value="payroll" data-testid="ledger-tab-payroll">Payroll Ledger</TabsTrigger>}
          {canViewAll && <TabsTrigger value="invoices" data-testid="ledger-tab-invoices">Client Invoices</TabsTrigger>}
        </TabsList>

        {!isCa && (
        <TabsContent value="payroll" className="mt-5">
          {isAdmin && (
            <div className="flex justify-end mb-3">
              <select
                data-testid="ledger-employee-select"
                onChange={(e) => setAddForEmployee(e.target.value)}
                defaultValue=""
                className="border border-[#E2E8F0] rounded-md text-sm px-3 py-1.5 mr-2"
              >
                <option value="" disabled>Select employee</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <Button
                size="sm"
                disabled={!addForEmployee}
                onClick={() => setAddDialogOpen(true)}
                data-testid="ledger-add-button"
                className="bg-[#76C121] hover:bg-[#5C9819] text-white"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Entry
              </Button>
            </div>
          )}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="payroll-ledger-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                  {isAdmin && <th className="px-5 py-3 font-medium">Employee</th>}
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Month</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Payout Date</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Receipt</th>
                  {isAdmin && <th className="px-5 py-3 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {payroll.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-8 text-center text-[#4B5563]">No payroll records yet.</td></tr>
                )}
                {payroll.map((p) => (
                  <tr key={p.id} data-testid={`payroll-row-${p.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                    {isAdmin && <td className="px-5 py-3 font-medium text-[#1C1C1E]">{p.employee_name}</td>}
                    <td className="px-5 py-3 text-[#4B5563] capitalize">{p.type}</td>
                    <td className="px-5 py-3 text-[#4B5563]">{p.month}</td>
                    <td className="px-5 py-3 text-[#1C1C1E] font-medium">{currency(p.amount)}</td>
                    <td className="px-5 py-3 text-[#4B5563]">{p.payout_date || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge value={p.status} /></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        {p.invoice_path ? (
                          <>
                            <a href={fileUrl(p.invoice_path)} target="_blank" rel="noreferrer" title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                            <a href={downloadUrl(p.invoice_path)} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                            {isAdmin && (
                              <>
                                <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`payroll-receipt-replace-${p.id}`} title="Replace receipt">
                                  <Pencil className="h-4 w-4" />
                                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadReceipt(p.employee_id, p.id, e.target.files[0])} />
                                </label>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button data-testid={`payroll-receipt-delete-${p.id}`} className="text-red-600" title="Delete receipt"><Trash2 className="h-4 w-4" /></button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Remove this receipt?</AlertDialogTitle>
                                      <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteReceipt(p.employee_id, p.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </>
                        ) : isAdmin ? (
                          <label className="cursor-pointer text-[#4B5563]" data-testid={`payroll-receipt-upload-${p.id}`} title="Upload receipt">
                            <Upload className="h-4 w-4" />
                            <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadReceipt(p.employee_id, p.id, e.target.files[0])} />
                          </label>
                        ) : "—"}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-5 py-3">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`payroll-delete-${p.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete this ledger entry?</AlertDialogTitle>
                              <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteLedgerEntry(p.employee_id, p.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        )}

        {canViewAll && (
          <TabsContent value="invoices" className="mt-5">
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="invoice-ledger-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
                    <th className="px-5 py-3 font-medium">Client</th>
                    <th className="px-5 py-3 font-medium">Invoice #</th>
                    <th className="px-5 py-3 font-medium">GST</th>
                    <th className="px-5 py-3 font-medium">Amount</th>
                    <th className="px-5 py-3 font-medium">Issue Date</th>
                    <th className="px-5 py-3 font-medium">Due Date</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">File</th>
                    <th className="px-5 py-3 font-medium">GST PDF</th>
                    <th className="px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 && (
                    <tr><td colSpan={10} className="px-5 py-8 text-center text-[#4B5563]">No invoices yet.</td></tr>
                  )}
                  {invoices.map((inv) => (
                    <tr key={inv.id} data-testid={`ledger-invoice-row-${inv.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                      <td className="px-5 py-3 font-medium text-[#1C1C1E]">{inv.client_name}</td>
                      <td className="px-5 py-3 text-[#4B5563]">{inv.invoice_number}</td>
                      <td className="px-5 py-3 text-xs text-[#4B5563]">{inv.gst_type && inv.gst_type !== "None" ? inv.gst_type : "—"}</td>
                      <td className="px-5 py-3 text-[#1C1C1E] font-medium">{currency(inv.amount)}</td>
                      <td className="px-5 py-3 text-[#4B5563]">{inv.issue_date}</td>
                      <td className="px-5 py-3 text-[#4B5563]">{inv.due_date}</td>
                      <td className="px-5 py-3"><StatusBadge value={inv.status} /></td>
                      <td className="px-5 py-3">
                        {inv.file_path ? (
                          <div className="flex items-center gap-2.5">
                            <a href={fileUrl(inv.file_path)} target="_blank" rel="noreferrer" title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                            <a href={downloadUrl(inv.file_path, inv.invoice_number)} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                            {isAdmin && (
                              <>
                                <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`ledger-invoice-file-replace-${inv.id}`} title="Replace file">
                                  <Pencil className="h-4 w-4" />
                                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadInvoiceFile(inv.client_id, inv.id, e.target.files[0])} />
                                </label>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <button data-testid={`ledger-invoice-file-delete-${inv.id}`} className="text-red-600" title="Delete file"><Trash2 className="h-4 w-4" /></button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Remove this invoice file?</AlertDialogTitle>
                                      <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteInvoiceFile(inv.client_id, inv.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                          </div>
                        ) : isAdmin ? (
                          <label className="cursor-pointer text-[#4B5563]" data-testid={`ledger-invoice-file-upload-${inv.id}`} title="Upload file">
                            <Upload className="h-4 w-4" />
                            <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadInvoiceFile(inv.client_id, inv.id, e.target.files[0])} />
                          </label>
                        ) : <span className="text-xs text-[#4B5563]">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <a href={`${API}/clients/${inv.client_id}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`ledger-invoice-gst-pdf-${inv.id}`} title="Download GST invoice PDF"><FileText className="h-4 w-4 text-[#4B5563] hover:text-[#00823B]" /></a>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {isAdmin && (
                            <button data-testid={`ledger-invoice-email-${inv.id}`} onClick={() => emailInvoice(inv.client_id, inv.id)} className="text-[#4B5563] hover:text-[#00823B]" title="Email invoice to client">
                              <Mail className="h-4 w-4" />
                            </button>
                          )}
                          <ReconciliationNoteButton entityType="invoice" entityId={inv.id} label={inv.invoice_number} />
                          {isAdmin && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button data-testid={`ledger-invoice-delete-${inv.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete invoice {inv.invoice_number}?</AlertDialogTitle>
                                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteInvoice(inv.client_id, inv.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {isAdmin && (
        <LedgerEntryFormDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} employeeId={addForEmployee} onCreated={load} />
      )}
    </DashboardLayout>
  );
}
